<template>
  <div>
    <header>
      <h4 v-t :hidden="routePath === '/app/revenue'">Revenue</h4>
      <div class="actions-wrapper">
        <!-- DATE PICKER -->
        <date-picker
          v-if="hasBilling"
          v-model="selectedMonth"
          minimum-view="month"
          maximum-view="month"
          :enabled-dates="enabledDates"
          @selected="pickMonth"
        />

        <button type="button" class="flat small icon before" @click="fetchFeeReport">
          <svgicon name="download" :fill="false" />
          <span v-t>Download fee report</span>
        </button>
      </div>
    </header>
    <section class="report revenue">
      <!-- WIDGET -->
      <div v-if="isLoading" class="card">
        <div class="spinner-container">
          <loading-spinner class="spinner" />
          <span>
            <span v-t>Crunching the numbers</span>
            <three-loading-dots />
          </span>
        </div>
      </div>

      <div v-else>
        <!-- SHOW PAYMENT LINKING -->
        <div v-if="showPaymentLinkWidget" class="card">
          <article class="revenue-onboard">
            <img src="~@/assets/images/money.png" :alt="$t('Money Flies')">
            <h5 v-if="selectedBilling.newPayments === 0" v-t>
              Add Your Payment Information.
            </h5>
            <h5 v-if="selectedBilling.newPayments > 0">
              <span v-t>We owe you</span>
              &nbsp; {{ selectedBilling.newPayments | formatCurrency }}
            </h5>
            <p v-t>For us to pay you, we need your payment information. Once that’s done, you can start getting paid!</p>
            <button v-t type="button" @click="togglePayment">
              Add Payment Information
            </button>

            <profile-payment v-if="showPayment" @close="togglePayment" />
          </article>
        </div>
        <!-- END SHOW PAYMENT LINKING -->

        <!-- SHOW BILLING -->
        <div v-else>
          <!-- HAS BILLING FLOW -->
          <div v-if="hasBilling" class="card min">
            <header>
              <h5>{{ selectedMonthLocale | formatMonth }}</h5>
            </header>
            <article>
              <dl>
                <dt>
                  <span v-t>Beginning Balance</span>
                  <span><em class="subtle">&nbsp;({{ selectedBilling.startDate | formatShortDateWithoutYear }})</em></span>
                </dt>
                <dd class="tab-numbers" :class="[{ negative: selectedBilling.startingBalance < 0 }, { positive: selectedBilling.startingBalance > 0 }]">
                  <span>{{ selectedBilling.startingBalance | formatCurrency }}</span>
                </dd>
              </dl>
              <dl>
                <dt v-t>Earned</dt>
                <dd class="tab-numbers" :class="[{ negative: selectedBilling.newBills < 0 }, { positive: selectedBilling.newBills > 0 }]">
                  <span>{{ selectedBilling.newBills | formatCurrency }}</span>
                </dd>
              </dl>
              <dl>
                <dt v-t>Paid</dt>
                <!--
                  Note that the value of this field is negated to appear that it is subtracted from
                  (beginningBalance + income) in order to determine endingBalance
                -->
                <dd class="tab-numbers" :class="[{ negative: selectedBilling.newPayments > 0 }, { positive: selectedBilling.newPayments < 0 }]">
                  <span>{{ -selectedBilling.newPayments | formatCurrency }}</span>
                </dd>
              </dl>
            </article>
            <hr>
            <footer>
              <dl>
                <dt>
                  <span><strong v-t>Ending Balance</strong></span>
                  <span class="subtle"><em>&nbsp;({{ selectedBilling.endDate | formatShortDateWithoutYear }})</em></span>
                </dt>
                <dd :class="[{ negative: selectedBilling.endingBalance < 0 }, { positive: selectedBilling.endingBalance > 0 }]" class="tab-numbers">
                  <strong>{{ selectedBilling.endingBalance | formatCurrency }}</strong>
                </dd>
              </dl>
            </footer>
          </div>
          <!-- END HAS BILLING FLOW -->

          <!-- NO BILLING FLOW YET -->
          <div v-else class="revenue-onboard card">
            <article class="revenue-onboard">
              <img src="~@/assets/images/bank.png" :alt="$t('Bank')">
              <p>
                <span v-t>You’ll see revenue reports here after your first month of using</span>
                &nbsp; {{ brandName }} &nbsp;
                <span v-t>with clients.</span>
              </p>
            </article>
            <!-- end account-linked -->
          </div>
          <!-- END NO BILLING FLOW YET -->
        </div>
      </div>
      <!-- END SHOW BILLING -->
      <!-- end card -->
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import { fetchFeeReport } from '@/lib/api';
import { mapGetters, mapActions, mapGetterMethods } from '@/lib/vue';
import { datePicker, loadingSpinner, threeLoadingDots } from '@/components/layout';
import '@/lib/icons/download';
import filters from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import profilePayment from './profile/payment_info';
import errorStore from '@/store/error';

const _ = { errorStore, fetchFeeReport };

const brandConfig = getBrandConfig();

export default {
  name: 'Billing',
  _,
  components: {
    profilePayment, datePicker, loadingSpinner, threeLoadingDots,
  },
  filters,

  data() {
    return {
      showPayment: false,
      showPaymentInformationLink: brandConfig.paymentInformationLink,
      selectedMonth: moment().add(-1, 'month').startOf('month').toDate(),
    };
  },

  computed: {
    ...mapGetters(['account', 'getFirstDate', 'isPaymentInfoSet', 'isLoadingBilling', 'isLoadingPaymentInfo']),

    selectedMonthLocale() {
      return moment(this.selectedMonth).toDate();
    },

    selectedBilling() {
      return this.getBilling(moment(this.selectedMonth).format('YYYY-MM-DD'));
    },

    hasBilling() {
      return this.hasBillingBefore(moment().startOf('month').format('YYYY-MM-DD'));
    },

    enabledDates() {
      if (!this.hasBilling) return { to: null, from: null };
      return {
        to: moment(this.getFirstDate).startOf('month').toDate(),
        from: moment().add(-1, 'month').startOf('month').toDate(),
      };
    },

    showPaymentLinkWidget() {
      return this.showPaymentInformationLink && !this.isPaymentInfoSet;
    },

    isLoading() {
      return this.isLoadingBilling || this.isLoadingPaymentInfo;
    },

    routePath() {
      if (!this.$route) return '';
      return this.$route.path;
    },

    brandName() {
      return (this.$lang === 'fr-CA' ? brandConfig.fullNameFr : brandConfig.fullNameEn);
    },
  },

  async created() {
    moment.locale(this.$lang);
    try {
      await Promise.all([
        this.fetchPaymentInfo(),
        this.fetchBilling(moment(this.selectedMonth).format('YYYY-MM-DD')),
      ]);
    } catch (exception) {
      _.errorStore.addError();
    }
  },

  methods: {
    ...mapGetterMethods(['getBilling', 'hasBillingBefore']),
    ...mapActions(['fetchBilling', 'fetchPaymentInfo', 'fetchAccount']),
    async pickMonth(date) {
      await this.fetchBilling(moment(date).format('YYYY-MM-DD'));
      this.selectedMonth = date;
    },

    togglePayment() {
      this.showPayment = !this.showPayment;
    },
    async fetchFeeReport() {
      try {
        const results = await Promise.all([
          fetchFeeReport.execute(),
          this.fetchAccount(),
        ]);
        const csv = results[0];
        const ymd = moment().format('YYYY-MM-DD');
        const filename = `fee_report_${this.account.firstName}_${this.account.lastName}_${ymd}.csv`.toLowerCase();

        const element = document.createElement('a');

        element.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`);
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
      } catch (exception) {
        _.errorStore.addError();
      }
    },
  },
};
</script>

<style scoped lang="scss">

  .actions-wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .revenue-onboard {
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > * { max-width: 100%; }

    h4 {
      text-align: center;
      margin-bottom: 0.5rem;
    }

    p { text-align: center; }

    img {
      width: 4rem;
      margin: 1rem 0;
    }

    button { margin: 1rem 0; }

    @media #{$screen-width-medium} {
      padding: 3rem 0.5rem;
    }
  }

  .spinner-container {
    margin: 3rem auto;
  }

  .positive::after {
    content: ")";
    visibility: hidden;
  }
</style>
